'use client';

import * as DS from '@aurorasolar/ds';
import { th, x } from '@xstyled/styled-components';
import { styled } from 'styled-components';

import { ButtonLink } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

// Marketing primary CTA is Borealis Maroon per the messaging review — yellow is
// accent-only on this card (border + icon). && outspecifies the DS variant styles.
const MaroonButtonLink = styled(ButtonLink)`
  && {
    background-color: ${th.color('maroon-800')};
    color: ${th.color('white')};
  }
  &&:hover:not([disabled]) {
    background-color: ${th.color('maroon-900')};
  }
`;

const DEFAULT_BODY = 'Get installer estimates before sharing your contact information.';
const DEFAULT_CTA_HREF = '/quote-builder';
const DEFAULT_CTA_LABEL = 'Get my estimate';
const DEFAULT_HEADING = 'See what installers near you are actually charging — before you talk to anyone.';
const DEFAULT_UTM_SOURCE = 'blog-cta-inline';

// Base only used to resolve relative author paths; a real host on `path` overrides it
const RELATIVE_BASE = 'https://placeholder.invalid';

// utm_* identify the placement; affid + inbound param passthrough stay owned by useCrossDomainHref.
// A malformed author-typed cta_href falls back to the default path instead of throwing during SSR
const appendUtmParams = (path: string, utmSource: string): string => {
  let url: URL;
  try {
    url = new URL(path, RELATIVE_BASE);
  } catch {
    url = new URL(DEFAULT_CTA_HREF, RELATIVE_BASE);
  }
  if (!url.searchParams.has('utm_campaign')) url.searchParams.set('utm_campaign', 'inline-cta-card');
  if (!url.searchParams.has('utm_medium')) url.searchParams.set('utm_medium', 'blog');
  if (!url.searchParams.has('utm_source')) url.searchParams.set('utm_source', utmSource);
  // Keep the full URL for an absolute (external) cta_href; only strip the base for relative paths
  return url.origin === RELATIVE_BASE ? `${url.pathname}${url.search}${url.hash}` : url.toString();
};

type Props = {
  body?: string;
  ctaHref?: string;
  ctaLabel?: string;
  /** Every placement should pass a page-specific heading — the generic default underperforms */
  heading?: string;
  /** Post pathname when rendered from server-parsed content, where usePathname is not the post URL */
  page?: string;
  utmSource?: string;
};

export const BlogCtaCard = ({
  body = DEFAULT_BODY,
  ctaHref = DEFAULT_CTA_HREF,
  ctaLabel = DEFAULT_CTA_LABEL,
  heading = DEFAULT_HEADING,
  page,
  utmSource = DEFAULT_UTM_SOURCE,
}: Props) => (
  <DS.Flex
    alignItems={{ md: 'center' }}
    aria-label="Get a solar estimate"
    as="aside"
    border="1px solid"
    borderColor="yellow-200"
    borderRadius={16}
    flexDirection={{ _: 'column', md: 'row' }}
    gap={5}
    my={6}
    p={6}
  >
    <DS.IconSun aria-hidden color="yellow-200" flexShrink={0} size={8} />
    <x.div flex={1}>
      <DS.Text as="p" fontWeight="medium" text={{ _: 'body16', md: 'body20' }}>
        {heading}
      </DS.Text>
      <DS.Text as="p" color="uiHelperCopy" mt={1} text="body14">
        {body}
      </DS.Text>
    </x.div>
    <MaroonButtonLink
      event={{ data: { placement: 'inline-card', utmSource, ...(page && { page }) }, name: TRACKING.BLOG_CTA_CLICK }}
      icon={DS.IconArrowRight}
      iconPosition="right"
      path={appendUtmParams(ctaHref, utmSource)}
      variant="cta"
    >
      {ctaLabel}
    </MaroonButtonLink>
  </DS.Flex>
);
